import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import styles from 'assets/jss/material-dashboard-pro-react/components/notificationPanelStyle';
import SingleNotification from './SingleNotification';
import { Notification } from 'utils/api/models/alertsAndNotifications/alertsAndNotifications';
import { Filters } from './constant';
import { getNotificationsLabels } from 'redux/modules/dynamicLabels/selectors';
import { useSelector } from 'react-redux';
import { AlertsAndNotificationsService } from 'services/alertsAndNotifications/alertsAndNotifications';
import { Checkbox, ListItemText } from '@material-ui/core';
import { getLanguage } from 'utils/language/language';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import { getDayDiff } from 'utils/date/getDate';
import { finalize } from 'rxjs/operators';
import { BodyMBold, DescriptionM } from 'components/Typography/Typography';
import Placeholder from 'components/Placeholder/Placeholder';
import { setToast } from 'redux/modules/toast/actions';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { upIdSelector } from 'redux/modules/session/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);
interface INotifications {
	show: boolean;
	hideNotification: () => void;
}

const NotificationPanel: React.FC<INotifications> = ({ show, hideNotification }) => {
	const classes = useStyles({
		read: false,
	});
	const notificationsLabels = useSelector(getNotificationsLabels);
	const [allNotifications, setAllNotifications] = useState<Record<string, Notification[]>>({});
	const [readNotifications, setReadNotifications] = useState<Record<string, Notification[]>>({});
	const [unReadNotifications, setUnReadNotifications] = useState<Record<string, Notification[]>>({});
	const [data, setData] = useState<Notification[]>([]);
	const [notificationLoading, setNotificationLoading] = useState(false);
	const [isAPIError, setIsAPIError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const accId = useSelector(upIdSelector);
	const limit = notificationsLabels.notification_limit || 50;
	const selectedFilter = [Filters.Read, Filters.Unread];
	const lang = getLanguage();

	const fetchNotifications = () => {
		AlertsAndNotificationsService.getNotifications(limit)
			.pipe(finalize(() => setNotificationLoading(false)))
			.subscribe(
				(res) => {
					setData(res?.response?.data);
					setErrorMessage('');
					setIsAPIError(false);
				},
				(error) => {
					setErrorMessage(notificationsLabels.fetchError.errors?.[error?.response?.code]);
					if (error?.status === 500) {
						setErrorMessage(notificationsLabels.fetchError.errors?.INTERNAL_ERROR);
					}
					setIsAPIError(true);
				}
			);
	};
	useEffect(() => {
		if (show) {
			setNotificationLoading(true);
			fetchNotifications();
		}
	}, [show]);

	useEffect(() => {
		if (data && data.length) {
			const allNotifications = AlertsAndNotificationsService.getNotificationsByLanguage(
				[...data],
				lang.toUpperCase()
			);

			setReadNotifications(
				groupBy(
					allNotifications?.filter((noti: Notification) => noti.status === Filters.Read),
					(notification) => moment(notification.createdAt).format('LL')
				)
			);
			setUnReadNotifications(
				groupBy(
					allNotifications?.filter((noti: Notification) => noti.status === Filters.Unread),
					(notification) => moment(notification.createdAt).format('LL')
				)
			);
			setAllNotifications(
				groupBy(allNotifications, (notification) => moment(notification.createdAt).format('LL'))
			);
		}
	}, [data, lang]);

	const updateReadNotification = (notificationId?: string): void => {
		setDataLayer('secondary_navigation', getLanguage(), null, accId, null, {
			link_name: 'notification:mark all as read',
		});
		if (notificationId) {
			AlertsAndNotificationsService.readNotification(notificationId).subscribe();
		} else {
			AlertsAndNotificationsService.readAllNotifications().subscribe(
				() => {},
				(error) => {
					setToast({
						error: true,
						label:
							error?.status === 500
								? notificationsLabels.fetchError.errors?.INTERNAL_ERROR
								: notificationsLabels.readError.errors?.[error.response?.code],
					});
				}
			);
		}
	};

	const notification: Record<string, Notification[]> | undefined = useMemo(() => {
		// Return all notifications if the filter has both read and unread notifications
		if (selectedFilter.length > 1) {
			return allNotifications;
		} else if (selectedFilter.includes(Filters.Read)) {
			return readNotifications;
		} else if (selectedFilter.includes(Filters.Unread)) {
			return unReadNotifications;
		} else {
			return {};
		}
	}, [selectedFilter, allNotifications, readNotifications, unReadNotifications]);

	const onDrawerClose = () => {
		/* to mark to notification read if there is any unread notification,
			unReadNotifications is an object having groupby data therefor using isEmpty from lodash
		*/
		if (!isEmpty(unReadNotifications)) updateReadNotification();
		setDataLayer('secondary_navigation', getLanguage(), null, accId, null, {
			link_name: 'notification:close',
		});
		hideNotification();
	};

	const EmptyState = ({
		icon,
		title,
		description,
		button,
	}: {
		icon?: boolean;
		title: string;
		description?: string;
		button?: {
			onClick: () => void;
			children: ReactNode;
		};
	}) => {
		return (
			<div className={classes.emptyMessage}>
				{icon && <img src="/empty-state.svg" alt="empty state" aria-hidden="true" />}
				<BodyMBold>{title}</BodyMBold>
				{description && <DescriptionM>{description}</DescriptionM>}
				{button && <Button {...button} color="primary" />}
			</div>
		);
	};

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="temporary"
				classes={{
					paper: classes.drawerPaper,
				}}
				anchor="right"
				open={show}
				onClose={onDrawerClose}
			>
				<div>
					<div className={classes.header}>
						<Typography variant="h6">{notificationsLabels?.notifications}</Typography>
						<IconButton onClick={onDrawerClose} aria-label={notificationsLabels?.closeNotification}>
							<CloseIcon />
						</IconButton>
					</div>
				</div>
				{notificationLoading ? (
					<Placeholder items={5} />
				) : (
					<>
						{Object.keys(notification).length > 0 ? (
							<List className={classes.list}>
								{Object.keys(notification).map((key) => {
									{
										return (
											<li key={key}>
												{notification[key]?.map(
													(
														{
															createdAt,
															title,
															message,
															notificationId,
															status,
															notificationSubType,
															parameters,
														},
														index
													) => (
														<SingleNotification
															key={`${notificationId}${index}`}
															createdAt={createdAt}
															title={title}
															id={notificationId}
															read={status === Filters.Read}
															description={message}
															accId={accId}
															notificationSubType={notificationSubType}
															parameters={parameters}
															hideNotification={hideNotification}
														/>
													)
												)}
											</li>
										);
									}
								})}
							</List>
						) : isAPIError ? (
							<EmptyState
								title={errorMessage}
								button={{
									onClick: fetchNotifications,
									children: notificationsLabels?.retry,
								}}
							/>
						) : (
							<EmptyState
								icon
								title={notificationsLabels?.fetchError?.empty?.title}
								description={notificationsLabels?.fetchError?.empty?.description}
							/>
						)}
					</>
				)}
			</Drawer>
		</>
	);
};

export default NotificationPanel;
